import {
  Add as AddIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  RemoveRedEye as ViewIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomButton from "../../common/button/commonButton";
import ReusableTable from "../../common/table/CommonTable";
import {
  addRole,
  deleteRole,
  fetchRoles,
  resetCurrentRole,
  updateRole,
} from "../../redux/role/roleAction";
import { ColoredCheckbox, ModuleChip, RoleChip } from "./commonactions";

const RolePermissionsManagement = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  // State
  const [selectedModule, setSelectedModule] = useState("all");
  const [selectedRole, setSelectedRole] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("add");
  const [selectedRoleData, setSelectedRoleData] = useState(null);
  const [rowsPerPage] = useState(isMobile ? 5 : 10);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const modules = [
    { id: 1, name: "adminDashboard" },
    { id: 2, name: "dashboard" },
    { id: 3, name: "patient" },
    { id: 4, name: "admissions" },
    { id: 5, name: "doctor" },
    { id: 6, name: "securityOfficer" },
    { id: 7, name: "staff" },
    { id: 8, name: "block" },
    { id: 9, name: "floor" },
    { id: 10, name: "ward" },
    { id: 11, name: "department" },
    { id: 12, name: "specialization" },
    { id: 13, name: "patientTransfer" },
    { id: 14, name: "adminTransfer" },
    { id: 15, name: "history" },
    { id: 16, name: "rolePermission" },
    { id: 17, name: "adminProfile" },
    { id: 18, name: "staffProfile" },
    { id: 19, name: "doctorProfile" },
    { id: 20, name: "staffPatientList" },
    { id: 21, name: "report" },
  ];

  const moduleNameMapping = {
    RolePerminsions: "rolePermission",
    floor: "floor",
  };

  const [rolesPermissionsData, setRolesPermissionsData] = useState([]);
  const [uniqueRoles, setUniqueRoles] = useState([]);

  useEffect(() => {
    dispatch(fetchRoles(currentPage));
  }, [dispatch, currentPage]);

  const roleState = useSelector((state) => state?.role?.fetchRoles || {});
  const addRoleState = useSelector((state) => state?.role?.addRole || {});
  const updateRoleState = useSelector((state) => state?.role?.updateRole || {});
  const deleteRoleState = useSelector((state) => state?.role?.deleteRole || {});

  const totalPages = roleState?.Roles?.totalPages || 0;
  useEffect(() => {
    if (rolesPermissionsData.length > 0) {
      const roles = [
        ...new Set(rolesPermissionsData.map((item) => item.roleName)),
      ];
      setUniqueRoles(roles);
    }
  }, [rolesPermissionsData]);

  useEffect(() => {
    if (addRoleState.success) {
      setSnackbarMessage("Role added successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      dispatch(fetchRoles());
      dispatch(resetCurrentRole());
    } else if (addRoleState.error) {
      if (
        addRoleState?.errorMessage === "Request failed with status code 400"
      ) {
        setSnackbarMessage(`Failed to add role: Role already exists`);
      } else {
        setSnackbarMessage(`Failed to add role: ${addRoleState.error}`);
      }
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      dispatch(resetCurrentRole());
    }
  }, [addRoleState, dispatch]);

  useEffect(() => {
    if (updateRoleState.success) {
      setSnackbarMessage("Role updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      dispatch(fetchRoles());
      dispatch(resetCurrentRole());
    } else if (updateRoleState.error) {
      setSnackbarMessage(`Failed to update role: ${updateRoleState.error}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      dispatch(resetCurrentRole());
    }
  }, [updateRoleState, dispatch]);

  useEffect(() => {
    if (deleteRoleState.success) {
      setSnackbarMessage("Role deleted successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      dispatch(fetchRoles());
      dispatch(resetCurrentRole());
    } else if (deleteRoleState.error) {
      setSnackbarMessage(`Failed to delete role: ${deleteRoleState.error}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      dispatch(resetCurrentRole());
    }
  }, [deleteRoleState, dispatch]);

  useEffect(() => {
    if (
      roleState.success &&
      roleState.Roles &&
      roleState.Roles.data &&
      roleState.Roles.data.length > 0
    ) {
      const formattedData = [];

      roleState.Roles.data.forEach((role) => {
        try {
          const permissions = JSON.parse(role.permissions);
          Object.keys(permissions).forEach((moduleName) => {
            const modulePermissions = permissions[moduleName];
            const normalizedModuleName =
              moduleNameMapping[moduleName] || moduleName;
            formattedData.push({
              id: `${role.id}-${moduleName}`,
              serialNO: role.serial_no || "N/A",
              roleName: role.name,
              moduleName: normalizedModuleName,
              add: modulePermissions.add || false,
              edit: modulePermissions.edit || false,
              view: modulePermissions.view || false,
              delete: modulePermissions.delete || false,
              allAccess: modulePermissions.allaccess || false,
              noAccess: modulePermissions.noaccess || false,
            });
          });
        } catch (error) {
          setSnackbarMessage(
            `Error parsing permissions for role: ${role.name}`
          );
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      });

      setRolesPermissionsData(formattedData);
    }
  }, [roleState]);

  const methods = useForm({
    defaultValues: {
      roleName: "",
      modules: {},
    },
  });

  const { control, handleSubmit, reset, setValue, getValues } = methods;

  // Filter data based on search query and selected filters
  const filteredData = rolesPermissionsData.filter((item) => {
    const matchesSearch = item.roleName
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesModuleFilter =
      selectedModule === "all" || item.moduleName === selectedModule;
    const matchesRoleFilter =
      selectedRole === "all" || item.roleName === selectedRole;

    return matchesSearch && matchesModuleFilter && matchesRoleFilter;
  });

  const processDataForTable = () => {
    const groupedByRole = {};

    filteredData.forEach((item) => {
      if (!groupedByRole[item.roleName]) {
        groupedByRole[item.roleName] = {
          serialNO: item.serialNO,
          roleId: item.id.length > 36 ? item.id.substring(0, 36) : item.id,
          roleName: item.roleName,
        };
      }
    });

    const tableData = [];
    Object.keys(groupedByRole).forEach((roleName) => {
      const roleData = groupedByRole[roleName];

      tableData.push({
        id: `${roleName}`,
        roleId: roleData.roleId,
        serialNO: roleData.serialNO,
        roleName: roleData.roleName,
        isFirstAction: true, // Always true since we only have one row per role
        isLastAction: true,
        actionCount: 1,
      });
    });

    return tableData;
  };

  const tableData = processDataForTable();

  // Responsive table columns - Simplified version showing only Serial No, Role Name, and Actions
  const getColumns = () => {
    const columns = [
      {
        id: "serialNO",
        label: "Serial No",
        render: (value, row) => {
          if (row.isFirstAction) {
            return value;
          }
          return null;
        },
      },
      {
        id: "roleName",
        label: "Role Name",
        render: (value, row) => {
          if (row.isFirstAction) {
            return (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                  height: "100%",
                }}
              >
                <RoleChip roleName={value} />
              </Box>
            );
          }
          return null;
        },
      },
      {
        id: "actions",
        label: "Actions",
        render: (_, row) => {
          if (row.isFirstAction) {
            return (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Tooltip title="View">
                  <Button
                    size="small"
                    variant="contained"
                    color="info"
                    onClick={() =>
                      handleViewRole({
                        roleName: row.roleName,
                        roleId: row.roleId,
                      })
                    }
                    sx={{
                      minWidth: "40px",
                      padding: "4px 8px",
                      mb: isMobile ? 1 : 0,
                    }}
                  >
                    <ViewIcon fontSize="small" />
                  </Button>
                </Tooltip>
                <Tooltip title="Edit">
                  <Button
                    size="small"
                    variant="contained"
                    sx={{
                      backgroundColor: "orange",
                      color: "white",
                      minWidth: "40px",
                      padding: "4px 8px",
                      mb: isMobile ? 1 : 0,
                    }}
                    onClick={() =>
                      handleEditRole({
                        roleName: row.roleName,
                        roleId: row.roleId,
                      })
                    }
                  >
                    <EditIcon fontSize="small" />
                  </Button>
                </Tooltip>
                <Tooltip title="Delete">
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    onClick={() =>
                      handleDeleteRole({
                        roleName: row.roleName,
                        roleId: row.roleId,
                      })
                    }
                    sx={{ minWidth: "40px", padding: "4px 8px" }}
                  >
                    <DeleteIcon fontSize="small" />
                  </Button>
                </Tooltip>
              </Box>
            );
          }
          return null;
        },
      },
    ];

    return columns;
  };

  // Helper function to create module column
  const createModuleColumn = (module) => ({
    id: `module-${module.id}`,
    label: module.name,
    render: (_, row) => {
      const modulePermission = row.modules.find((m) => m.name === module.name);
      const value = modulePermission ? modulePermission.value : false;
      const getActionColor = (action) => {
        const actionColors = {
          view: "#2196F3",
          add: "#4CAF50",
          edit: "#FF9800",
          delete: "#F44336",
          allAccess: "#673AB7",
          noAccess: "#9E9E9E",
        };
        return actionColors[action] || "#90A4AE";
      };

      return (
        <Box sx={{ textAlign: "center" }}>
          <ColoredCheckbox
            checked={value}
            color={getActionColor(row.actionType)}
            onChange={() => {}}
            disabled={true}
          />
        </Box>
      );
    },
  });

  const handleOpenDialog = (mode, rowData = null) => {
    setDialogMode(mode);

    if (mode === "add") {
      reset({
        roleName: "",
        modules: initializeModulePermissions(),
      });
    } else if (rowData) {
      const roleName = rowData.roleName;
      setSelectedRoleData(rowData);
      const rolePermissions = rolesPermissionsData.filter(
        (item) => item.roleName === roleName
      );

      const formattedPermissions = {};
      modules.forEach((module) => {
        const modulePermission = rolePermissions.find(
          (p) => p.moduleName === module.name
        );

        if (modulePermission) {
          formattedPermissions[module.id] = {
            add: modulePermission.add,
            edit: modulePermission.edit,
            view: modulePermission.view,
            delete: modulePermission.delete,
            allAccess: modulePermission.allAccess,
            noAccess: modulePermission.noAccess,
          };
        } else {
          formattedPermissions[module.id] = {
            add: false,
            edit: false,
            view: false,
            delete: false,
            allAccess: false,
            noAccess: false,
          };
        }
      });

      reset({
        roleName: roleName,
        modules: formattedPermissions,
      });
    }

    setDialogOpen(true);
  };

  const initializeModulePermissions = () => {
    const initialPermissions = {};
    modules.forEach((module) => {
      initialPermissions[module.id] = {
        add: false,
        edit: false,
        view: false,
        delete: false,
        allAccess: false,
        noAccess: false,
      };
    });
    return initialPermissions;
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handlePermissionChange = (moduleId, permission, value) => {
    const currentValues = getValues(`modules.${moduleId}`);

    const newState = { ...currentValues };
    newState[permission] = value;

    if (permission === "allAccess" && value === true) {
      newState.add = true;
      newState.edit = true;
      newState.view = true;
      newState.delete = true;
      newState.allAccess = true;
      newState.noAccess = false;
    } else if (permission === "noAccess" && value === true) {
      newState.add = false;
      newState.edit = false;
      newState.view = false;
      newState.delete = false;
      newState.allAccess = false;
      newState.noAccess = true;
    } else if (["add", "edit", "view", "delete"].includes(permission)) {
      newState.allAccess =
        newState.add && newState.edit && newState.view && newState.delete;
      newState.noAccess =
        !newState.add && !newState.edit && !newState.view && !newState.delete;
    }

    Object.keys(newState).forEach((key) => {
      setValue(`modules.${moduleId}.${key}`, newState[key]);
    });
  };

  const onSubmit = (data) => {
    const permissionsObject = {};
    modules.forEach((module) => {
      const modulePermission = data.modules[module.id] || {};
      let apiModuleName = module.name;

      // Convert display names to API names if needed
      // Reverse mapping for special cases
      if (module.name === "rolePermission") {
        apiModuleName = "RolePerminsions";
      } else if (module.name === "patientTransfer") {
        apiModuleName = "patientTransfer";
      }

      permissionsObject[apiModuleName] = {
        add: modulePermission.add || false,
        edit: modulePermission.edit || false,
        view: modulePermission.view || false,
        delete: modulePermission.delete || false,
        allaccess: modulePermission.allAccess || false,
        noaccess: modulePermission.noAccess || false,
      };
    });

    const rolePayload = {
      name: data.roleName,
      status: "1",
      permissions: permissionsObject,
    };

    if (dialogMode === "add") {
      dispatch(addRole([rolePayload]));
    } else if (dialogMode === "edit" && selectedRoleData) {
      dispatch(updateRole(selectedRoleData.roleId, [rolePayload]));
    }

    handleCloseDialog();
  };

  const handleViewRole = (row) => {
    const rolePermissions = rolesPermissionsData.filter(
      (item) => item.roleName === row.roleName
    );
    if (rolePermissions.length > 0) {
      handleOpenDialog("view", {
        roleName: row.roleName,
        serialNO: rolePermissions[0].serialNO,
        roleId: row.roleId,
      });
    }
  };

  const handleEditRole = (row) => {
    const rolePermissions = rolesPermissionsData.filter(
      (item) => item.roleName === row.roleName
    );
    if (rolePermissions.length > 0) {
      handleOpenDialog("edit", {
        roleName: row.roleName,
        serialNO: rolePermissions[0].serialNO,
        roleId: row.roleId,
      });
    }
  };

  const handleDeleteRole = (row) => {
    dispatch(deleteRole(row.roleId));
  };

  const handleAddRole = () => {
    handleOpenDialog("add");
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleModuleFilterChange = (event) => {
    setSelectedModule(event.target.value);
  };

  const handleRoleFilterChange = (event) => {
    setSelectedRole(event.target.value);
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedModule("all");
    setSelectedRole("all");
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  return (
    <Box p={isMobile ? 1 : 3} sx={{ backgroundColor: "white" }}>
      <Typography
        variant={isMobile ? "h5" : "h4"}
        mb={2}
        fontWeight="bold"
        color="#13a7b9"
        sx={{
          borderBottom: "2px solid #13a7b9",
          paddingBottom: 1,
          display: "inline-block",
        }}
      >
        Healthcare Role Permissions
      </Typography>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Search and Filter Section */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search roles..."
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearchQuery("")} size="small">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            size="small"
          />
        </Grid>

        <Grid item xs={6} sm={3} md={2}>
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={toggleFilters}
            fullWidth
            size="small"
            sx={{
              backgroundColor: "#000",
              borderColor: "#fff",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#e0f7fa",
                borderColor: "#13a7b9",
                color: "#13a7b9",
              },
            }}
          >
            filter
          </Button>
        </Grid>

        <Grid item xs={6} sm={3} md={2} sx={{ ml: "auto" }}>
          <CustomButton
            text="Add Role"
            type="submit"
            startIcon={<AddIcon />}
            onClick={handleAddRole}
            fullWidth
          />
        </Grid>
      </Grid>

      {/* Filter options */}
      {showFilters && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Filter by Module
              </Typography>
              <Select
                value={selectedModule}
                onChange={handleModuleFilterChange}
                displayEmpty
              >
                <MenuItem value="all">All Modules</MenuItem>
                {modules.map((module) => (
                  <MenuItem key={module.id} value={module.name}>
                    {module.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Filter by Role
              </Typography>
              <Select
                value={selectedRole}
                onChange={handleRoleFilterChange}
                displayEmpty
              >
                <MenuItem value="all">All Roles</MenuItem>
                {uniqueRoles.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Box sx={{ pt: 3 }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                fullWidth
                size="small"
              >
                Clear Filters
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}

      <Box sx={{ mb: 3, overflowX: "auto" }}>
        <ReusableTable
          title=""
          columns={getColumns()}
          data={tableData}
          headerColor="#000"
          rowsPerPage={rowsPerPage}
          customEmptyMessage="No permission data available"
          withPagination={true}
          withActions={false}
          showDownloadOption={true}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </Box>

      {/* Role Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor:
              dialogMode === "add"
                ? "#4CAF50"
                : dialogMode === "edit"
                ? "#FF9800"
                : "#2196F3",
            color: "white",
            fontWeight: "bold",
          }}
        >
          <Typography variant="h6">
            {dialogMode === "add"
              ? "Add New Role"
              : dialogMode === "edit"
              ? "Edit Role"
              : "View Role Details"}
          </Typography>
        </DialogTitle>
        <Divider />

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    name="roleName"
                    control={control}
                    rules={{ required: "Role name is required" }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Role Name"
                        fullWidth
                        disabled={dialogMode === "view"}
                        required
                        margin="normal"
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    fontWeight="bold"
                    sx={{
                      borderBottom: "2px solid #13a7b9",
                      paddingBottom: 1,
                      display: "inline-block",
                    }}
                  >
                    Module Permissions
                  </Typography>

                  {modules.map((module) => (
                    <Paper
                      key={module.id}
                      elevation={1}
                      sx={{
                        p: 2,
                        my: 2,
                        borderRadius: 2,
                        borderLeft: "4px solid",
                        borderColor: (() => {
                          const colors = [
                            "#4CAF50",
                            "#2196F3",
                            "#FF9800",
                            "#F44336",
                            "#673AB7",
                            "#E91E63",
                          ];
                          return colors[module.id % colors.length];
                        })(),
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          transform:
                            dialogMode !== "view" ? "translateY(-2px)" : "none",
                        },
                      }}
                    >
                      <Grid container>
                        <Grid
                          item
                          xs={6}
                          sm={3}
                          md={3}
                          style={{ maxWidth: "18%" }}
                        >
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            gutterBottom
                          >
                            <ModuleChip moduleName={module.name} />
                          </Typography>
                        </Grid>

                        {[
                          "add",
                          "edit",
                          "view",
                          "delete",
                          "allAccess",
                          "noAccess",
                        ].map((permission) => {
                          // Get color based on permission type
                          const getPermissionColor = () => {
                            const colors = {
                              add: "#4CAF50",
                              edit: "#FF9800",
                              view: "#2196F3",
                              delete: "#F44336",
                              allAccess: "#673AB7",
                              noAccess: "#9E9E9E",
                            };
                            return colors[permission];
                          };

                          // Get label for permission
                          const getPermissionLabel = () => {
                            const labels = {
                              add: "Add",
                              edit: "Edit",
                              view: "View",
                              delete: "Delete",
                              allAccess: "All Access",
                              noAccess: "No Access",
                            };
                            return labels[permission];
                          };

                          return (
                            <Grid
                              item
                              xs={6}
                              sm={2}
                              md={2}
                              key={permission}
                              style={{ maxWidth: "107px" }}
                            >
                              <Tooltip
                                title={`${getPermissionLabel()} permission`}
                              >
                                <Controller
                                  name={`modules.${module.id}.${permission}`}
                                  control={control}
                                  render={({ field }) => (
                                    <FormControlLabel
                                      control={
                                        <ColoredCheckbox
                                          checked={field.value || false}
                                          onChange={(e) => {
                                            field.onChange(e.target.checked);
                                            handlePermissionChange(
                                              module.id,
                                              permission,
                                              e.target.checked
                                            );
                                          }}
                                          color={getPermissionColor()}
                                          disabled={dialogMode === "view"}
                                        />
                                      }
                                      label={getPermissionLabel()}
                                    />
                                  )}
                                />
                              </Tooltip>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Paper>
                  ))}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions
              sx={{
                p: 2,
                backgroundColor: "#f5f5f5",
                borderTop: "1px solid #e0e0e0",
              }}
            >
              <CustomButton
                text="Cancel"
                type="cancel"
                onClick={handleCloseDialog}
              />
              {dialogMode !== "view" && (
                <CustomButton text="Submit" type="submit" />
              )}
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>
    </Box>
  );
};

export default RolePermissionsManagement;
